import { useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/common";
import { useFetch } from "@/hooks/useFetch";
import { money, fmtDate } from "@/lib/format";
import { downloadCsv } from "@/lib/csv";
import { cn } from "@/lib/utils";

export const CommissionReport = ({ year }) => {
  const { data } = useFetch("/reports/commission", { year });
  const [mode, setMode] = useState("revenue");
  const people = data || [];
  const months = Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, "0")}`);
  const cell = (m) => (mode === "revenue" ? money(m.revenue) : m.jobs);
  const total = (k) => people.reduce((s, p) => s + p[k], 0);

  const exportCsv = () => {
    const lines = ["Sales person,Jobs,Revenue,Deposits,Rate %,Commission owed," + months.map((m) => fmtDate(`${m}-01`, "MMM") + " jobs," + fmtDate(`${m}-01`, "MMM") + " revenue").join(",")];
    people.forEach((p) => lines.push([`"${p.name}"`, p.jobs, p.revenue.toFixed(2), p.deposits.toFixed(2), p.rate_pct, p.commission.toFixed(2), ...months.flatMap((m) => [p.months[m].jobs, p.months[m].revenue.toFixed(2)])].join(",")));
    downloadCsv(`commission-${year}.csv`, lines.join("\r\n"));
  };

  return (
    <Panel title={`Sales commission – booked revenue by sales person (${year})`} action={
      <div className="flex items-center gap-2">
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" data-testid="commission-mode">
          {["revenue", "jobs"].map((m) => <button key={m} onClick={() => setMode(m)} data-testid={`commission-mode-${m}`} className={cn("rounded-md px-3 py-1 text-xs font-medium capitalize", mode === m ? "bg-[#0E1E38] text-white" : "text-slate-600")}>{m}</button>)}
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!people.length} data-testid="commission-export"><Download className="h-4 w-4" /> CSV</Button>
      </div>}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px] text-xs" data-testid="commission-table">
          <thead>
            <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <th className="sticky left-0 bg-white pb-2 pr-3">Sales person</th><th className="pb-2 pr-3 text-right">Jobs</th><th className="pb-2 pr-3 text-right">Revenue</th><th className="pb-2 pr-3 text-right">Deposits</th><th className="pb-2 pr-3 text-right">Rate</th><th className="pb-2 pr-3 text-right">Commission owed</th>
              {months.map((m) => <th key={m} className="pb-2 pr-2 text-right">{fmtDate(`${m}-01`, "MMM")}</th>)}
            </tr>
          </thead>
          <tbody>
            {people.length === 0 && <tr><td colSpan={16} className="py-8 text-center text-slate-500" data-testid="commission-empty">No jobs in {year}.</td></tr>}
            {people.map((p) => (
              <tr key={p.name} className="border-t border-slate-100" data-testid={`commission-row-${p.name}`}>
                <td className="sticky left-0 bg-white py-2 pr-3 font-semibold text-slate-900">{p.name}</td>
                <td className="py-2 pr-3 text-right font-mono">{p.jobs}</td>
                <td className="py-2 pr-3 text-right font-mono font-semibold">{money(p.revenue)}</td>
                <td className="py-2 pr-3 text-right font-mono text-emerald-700">{money(p.deposits)}</td>
                <td className="py-2 pr-3 text-right font-mono text-slate-500">{p.rate_pct ? `${p.rate_pct}%` : "–"}</td>
                <td className="py-2 pr-3 text-right font-mono font-bold text-[#0E1E38]" data-testid={`commission-owed-${p.name}`}>{p.rate_pct ? money(p.commission) : "–"}</td>
                {months.map((m) => <td key={m} className={cn("py-2 pr-2 text-right font-mono", !p.months[m].jobs && "text-slate-300")}>{p.months[m].jobs ? cell(p.months[m]) : "–"}</td>)}
              </tr>
            ))}
          </tbody>
          {people.length > 0 && <tfoot><tr className="border-t-2 border-[#0E1E38] font-semibold"><td className="sticky left-0 bg-white py-2 pr-3">Total</td><td className="py-2 pr-3 text-right font-mono">{total("jobs")}</td><td className="py-2 pr-3 text-right font-mono">{money(total("revenue"))}</td><td className="py-2 pr-3 text-right font-mono">{money(total("deposits"))}</td><td /><td className="py-2 pr-3 text-right font-mono font-bold">{money(total("commission"))}</td>
            {months.map((m) => { const j = people.reduce((s, p) => s + p.months[m].jobs, 0); const r = people.reduce((s, p) => s + p.months[m].revenue, 0); return <td key={m} className="py-2 pr-2 text-right font-mono">{j ? (mode === "revenue" ? money(r) : j) : "–"}</td>; })}
          </tr></tfoot>}
        </table>
      </div>
    </Panel>
  );
};
